import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/shared/api/auth.api';
import { clearClientSessionState } from '@/shared/libs/session-cleanup';
import { PATHS } from '@/shared/constants/app/routes.constant';
import type {
  CheckAvailabilityRequest,
  ConsentItem,
  LoginRequest,
  OnboardingState,
  RegisterRequest,
  RequestPasswordResetRequest,
  SignupRecoveryEmailRequest,
  SignupRecoveryLookupRequest,
} from '@/shared/types/auth.type';
import { authKeys } from './auth.keys';

export function useLogin() {
  const qc = useQueryClient();

  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: () => {
      // 로그인 응답 user는 부분 데이터(name 누락 등)일 수 있어 캐시에 시드하지 않는다.
      // /me 를 단일 소스로 강제 → 로그인 직후와 새로고침 후 표시가 항상 일치.
      // (auth_state 쿠키는 서버가 set-cookie로 내려줬으므로 useMe가 즉시 refetch)
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useSignup() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (body: RegisterRequest) => authApi.signup(body),
  });
}

/**
 * 가입 위저드 계정 스텝 → '다음' 진입 전 이메일/아이디 중복 확인.
 * 조회지만 POST(비파괴적 검사) + 폼 진행 트리거이므로 useMutation 으로 명령형 호출.
 */
export function useCheckAvailability() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (body: CheckAvailabilityRequest) =>
      authApi.checkAvailability(body),
  });
}

export function useLogout() {
  const qc = useQueryClient();

  return useMutation({
    // 로그아웃은 실패해도 아래 onSettled 가 쿠키·캐시를 정리해 UI 상 로그아웃이 완료된다.
    // 이 상태에서 "로그아웃 실패" 팝업을 띄우면 사용자에게 모순된 신호를 준다.
    // (인증 폼의 인라인 표시와는 다른 사유 — 여기엔 보여줄 폼 자체가 없다.)
    meta: { skipGlobalError: true },
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // 서버가 clearCookie 로 모든 인증 쿠키를 지우지만,
      // 네트워크 실패 상황에도 UI 가 로그인 상태로 남지 않게 프론트에서도 정리.
      // 캐시뿐 아니라 storage 에 남은 사용자별 상태(조직 선택·결제 의도)까지 지운다 —
      // 공용 PC 에서 다음 사용자에게 그대로 넘어가는 값들이다.
      clearClientSessionState();
      qc.clear();

      // 하드 리다이렉트여야 한다. `qc.clear()` 는 캐시에서 query 를 지울 뿐
      // 이미 붙어있는 observer 를 깨우지 않아서, 보호 페이지의 AuthGuard 가
      // 직전 me 결과를 든 채 리렌더 없이 그대로 머문다.
      // SPA navigate 로는 OrgProvider 등 메모리 컨텍스트도 남는다.
      window.location.replace(PATHS.HOME);
    },
  });
}

// ── 이메일 인증 재발송 ───────────────────────────────────────────────

/**
 * 비로그인 재발송. 로그인 시 EMAIL_NOT_VERIFIED(403) 응답을 받은 사용자가 사용.
 * 백엔드는 email 만 받고 INACTIVE 사용자에게만 발송 — 존재/상태는 응답에 노출하지 않음.
 * 60초 쿨다운(429 AUTH_EMAIL_VERIFY_RESEND_COOLDOWN)은 서버가 email_verifications 로 검증.
 */
export function useResendVerificationAnonymous() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (email: string) => authApi.resendVerificationAnonymous(email),
  });
}

/** 로그인 화면 미인증 안내 → 가입된 주소로 인증 메일 재발송(아이디·비밀번호로 계정 특정). */
export function useResendVerificationByLogin() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (body: LoginRequest) => authApi.resendVerificationByLogin(body),
  });
}

/** 가입정보 확인 — 휴대폰 OTP 통과 후 미인증 계정의 가입 이메일 조회. */
export function useSignupRecoveryLookup() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (body: SignupRecoveryLookupRequest) =>
      authApi.signupRecoveryLookup(body),
  });
}

/** 가입정보 확인 후 이메일 재설정 — 새 주소로 인증 메일이 간다. */
export function useSignupRecoveryEmail() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (body: SignupRecoveryEmailRequest) =>
      authApi.signupRecoveryEmail(body),
  });
}

// ── 이메일 변경 ──────────────────────────────────────────────────────

/** 이메일 변경 요청 (마이페이지) — 새 이메일로 확인 링크 발송 + 현재 비밀번호 재확인. */
export function useRequestEmailChange() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (params: { newEmail: string; currentPassword: string }) =>
      authApi.requestEmailChange(params.newEmail, params.currentPassword),
  });
}

// ── 비밀번호 재설정 ──────────────────────────────────────────────────

export function useRequestPasswordReset() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (body: RequestPasswordResetRequest) =>
      authApi.requestPasswordReset(body),
  });
}

/**
 * 휴대폰 아이디 찾기. 실패 사유(인증 미완료 등)를 화면이 인라인으로 보여준다.
 * 성공 응답에 계정 목록이 실린다 — OTP 로 번호 소유를 이미 증명했기 때문.
 */
export function useFindLoginIdByPhone() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (phone: string) => authApi.findLoginIdByPhone(phone),
  });
}

/** 이메일 아이디 찾기 — 계정 유무와 무관하게 같은 응답이 온다(열거 방지). */
export function useFindLoginIdByEmail() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (email: string) => authApi.findLoginIdByEmail(email),
  });
}

// ── 첫 로그인 온보딩 ────────────────────────────────────────────
// 모든 단계가 끝나면 서버가 계정을 ACTIVE 로 올린다. 그 판정이 me 응답의
// status 를 바꾸므로 성공할 때마다 온보딩 상태와 me 를 함께 갱신한다.
// 응답의 상태를 곧바로 캐시에 넣는다 — refetch 를 기다리는 사이 이전 단계가 한 번 더 그려지지 않게.

/**
 * 계정 설정 단계 — 새 비밀번호(아직이면) → 필수 약관 동의(남았으면)를 한 번에 제출한다.
 * 비밀번호만 통과하고 동의가 실패해도 다시 제출하면 남은 동의만 보낸다(상태가 갱신돼 있다).
 */
export function useOnboardingAccount() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (input: {
      newPassword?: string;
      consents?: ConsentItem[];
    }) => {
      let state: OnboardingState | undefined;
      if (input.newPassword) {
        state = await authApi.onboardingPassword({
          newPassword: input.newPassword,
        });
        qc.setQueryData(authKeys.onboarding, state);
      }
      if (input.consents) {
        state = (await authApi.submitConsents(input.consents)).onboarding;
        await authApi.refresh().catch(() => undefined);
      }
      return state;
    },
    onSettled: (state) => {
      if (state) qc.setQueryData(authKeys.onboarding, state);
      qc.invalidateQueries({ queryKey: authKeys.onboarding });
      qc.invalidateQueries({ queryKey: authKeys.consents });
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

/** 이관 계정 아이디 재설정. 화면 상단의 로그인 아이디도 새 값으로 바뀐다. */
export function useOnboardingLoginId() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (loginId: string) => authApi.onboardingLoginId(loginId),
    onSuccess: (state) => {
      qc.setQueryData(authKeys.onboarding, state);
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

/** 이메일 인증번호 발송. 새 주소를 정한 경우(이관 계정) 화면의 주소가 바뀐다. */
export function useOnboardingEmail() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (email?: string) => authApi.onboardingEmail(email),
    onSuccess: (_data, email) => {
      if (!email) return;
      qc.invalidateQueries({ queryKey: authKeys.onboarding });
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useOnboardingEmailVerify() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (code: string) => authApi.onboardingEmailVerify(code),
    onSuccess: (state) => {
      qc.setQueryData(authKeys.onboarding, state);
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

/** 온보딩 휴대폰 인증번호 발송 — 가입과 같은 OTP 경로를 쓴다. */
export function useSendPhoneOtp() {
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (phone: string) => authApi.sendPhoneOtp(phone),
  });
}

/**
 * 약관 동의 제출(온보딩·재동의 공통). 성공하면 토큰을 갱신한다 — Gateway 는 토큰의
 * status·재동의 클레임으로 판정하므로, 갱신 전 토큰으로는 계속 막힌다.
 */
export function useSubmitConsents() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (items: ConsentItem[]) => {
      const state = await authApi.submitConsents(items);
      await authApi.refresh().catch(() => undefined);
      return state;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: authKeys.onboarding });
      qc.invalidateQueries({ queryKey: authKeys.consents });
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

/**
 * 세션 클레임 갱신. 온보딩 완료처럼 서버에서 계정 상태가 바뀐 직후, 새 상태가 실린
 * 토큰을 받는다(Gateway 는 토큰의 status 로 판정한다). 실패 안내는 호출부가 정한다.
 */
export function useRefreshSession() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: () => authApi.refresh(),
    onSettled: () => qc.invalidateQueries({ queryKey: authKeys.me }),
  });
}

/** 휴대폰 인증번호 확인 → 곧바로 온보딩 반영. 확인 뒤 버튼을 한 번 더 누르지 않게 묶는다. */
export function useOnboardingPhone() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (body: { phone: string; code: string }) => {
      await authApi.verifyPhoneOtp(body.phone, body.code);
      return authApi.onboardingPhone({ phone: body.phone });
    },
    onSuccess: (state) => {
      qc.setQueryData(authKeys.onboarding, state);
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

/** 휴대폰 단계 "나중에 하기" — 마감 전 이관 공유 계정만. 성공하면 온보딩이 끝나 화면이 refresh 후 이동한다. */
export function useDeferOnboardingPhone() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: () => authApi.onboardingPhoneDefer(),
    onSuccess: (state) => {
      qc.setQueryData(authKeys.onboarding, state);
      qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    // 인증 폼은 에러를 입력 필드 아래에 인라인으로 보여준다 — 팝업까지 뜨면 이중 안내다.
    meta: { skipGlobalError: true },
    mutationFn: (params: { token: string; newPassword: string }) =>
      authApi.resetPassword(params.token, params.newPassword),
  });
}
